import { api, CLAIM_API_SUB_URLS, MEMBER_API_SUB_URLS, GROUP_API_URL_SUB_URLS, V2 } from '@/api';
import { AuthService } from '@/services';
import moment from 'moment';
import { dateFormat } from '@/constants';
import { Module } from 'vuex/types';
import { AxiosResponse } from 'axios';
import {
  RootState,
  UserModel,
  UserGroupResponseModel,
  MemberMessageResponseModel,
  HeadersResponseModel,
  MemberPreferenceInformationResponseModel,
} from '@/@types/member';

interface CoreState {
  user: UserModel;
  userGroups: UserGroupResponseModel[];
  carrier: string;
  previousRoute: string;
  notifications: MemberMessageResponseModel[];
  // TODO Remove? I don't see this being set anywhere in the codebase.
  alerts: any[];
  memberPreferenceInformation: MemberPreferenceInformationResponseModel;
  isCobranded: boolean;
  chooseGroup: boolean;
  memberMigrationComplete: boolean;
  showAnnouncement: boolean;
}

const CORE_STATE: Module<CoreState, RootState> = {
  state: {
    user: null,
    userGroups: [],
    carrier: 'Blue Cross Blue Shield',
    previousRoute: '',
    notifications: [],
    alerts: [],
    memberPreferenceInformation: null,
    isCobranded: false,
    chooseGroup: false,
    memberMigrationComplete: false,
    showAnnouncement: true,
  },
  actions: {
    setChooseGroup(context) {
      context.commit('SET_CHOOSE_GROUP');
    },
    setMemberMigrationComplete(context) {
      context.commit('SET_MEMBER_MIGRATION_COMPLETE');
    },
    getUserGroups(context): Promise<AxiosResponse> {
      return api
        .get(`${GROUP_API_URL_SUB_URLS.users}/me/groups`)
        .then((userGroups) => {
          context.commit('SET_USER_GROUPS', userGroups.data);
          return userGroups.data;
        });
    },
    setUserObjectWithToken(context, JWT) {
      return new Promise((resolve, reject) => {
        AuthService.setUserObject(JWT)
          .then((user) => {
            context.commit('loginUser', user);
            resolve();
          })
          .catch(reject);
      });
    },
    restoreSession(context): Promise<any> {
      return new Promise((resolve, reject) => {
        AuthService.getSession()
          .then((user) => {
            context.commit('loginUser', user);
            resolve();
          })
          .catch(reject);
      });
    },
    loginUser(context, credentials): Promise<any> {
      return new Promise((resolve, reject) => {
        AuthService.signIn(credentials)
          .then((result) => {
            if (result && result.message === 'mfa-challenge') {
              resolve(result);
              return;
            }
            context.commit('loginUser', result);
            resolve();
          })
          .catch(reject);
      });
    },
    async respondToMFAChallenge(context, MFAResponse: {MFACode: string, challengeResponse: any, rememberDevice: boolean}) {
      await AuthService.respondToMFAChallenge(MFAResponse.MFACode, MFAResponse.challengeResponse, MFAResponse.rememberDevice);
      // looks like in the existing loginUser we commit the return value of signIn which is actually empty after a successful sign in.
      // Recreating that here in case something breaks without it.
      context.commit('loginUser', undefined);
    },
    // UC-REST-002 - Get Member Header
    getMemberHeader(context, memberId): Promise<AxiosResponse> {
      return api
        .get(
          `${MEMBER_API_SUB_URLS.members}/${memberId}/headers`,
        )
        .then((headers) => {
          context.commit('updateHeaders', headers.data);
          return headers.data;
        });
    },
    // UC-API-055
    getMemberPreferenceInformation(context): Promise<AxiosResponse> {
      return api
        .get(
          `${MEMBER_API_SUB_URLS.members}/${
            context.getters.user.memberId
          }/profiles/communication-preferences`,
        )
        .then((preferences) => {
          context.commit('setMemberPreferenceInformation', preferences.data);
          return preferences.data;
        });
    },
    // UC-REST-582
    updateMemberSMSCommunication(context, updatedPreferences): Promise<AxiosResponse> {
      const isSMSCommunication = {
        'isSMSCommunication': updatedPreferences.textIndicator,
      };

      return api
        .patch(`${MEMBER_API_SUB_URLS.members}/${
          context.getters.user.memberId
        }/sms-communication`, isSMSCommunication)
        .then((preferences) => {
          context.commit('setMemberPreferenceInformation', preferences.data);
          return preferences.data;
        });
    },
    setPreviousRoute(context, route): void {
      context.commit('setPreviousRoute', route);
    },
    // UC-REST-005 - Get Member Messages
    getNotifications(context): Promise<AxiosResponse> {
      return api
        .get(
          `${MEMBER_API_SUB_URLS.members}/${
            context.getters.user.memberId
          }/messages${V2}`,
        )
        .then((notifications) => {
          context.commit('setNotifications', notifications.data);
        });
    },
    // UC-REST-504 - Add Member Claim Request
    addMemberClaimRequest(context, options): Promise<AxiosResponse> {
      return api
        .post(
          `${CLAIM_API_SUB_URLS.members}/${
            context.getters.user.memberId
          }/claims`,
          options,
        )
        .then((claim) => {
          return claim.data;
        });
    },
    // UC-REST-006 - Update Member Message
    markNotificationAsRead(context, messageId): Promise<AxiosResponse> {
      return api
        .post(`${MEMBER_API_SUB_URLS.members}/${context.getters.user.memberId}/message`,
        {
          messageId,
        })
        .then(() => {
          context.commit('setNotificationAsRead', messageId);
        });
    },
    // UC-REST-002
    getHeaders(context): Promise<AxiosResponse> {
      return api
        .get(
          `${MEMBER_API_SUB_URLS.members}/${
            context.getters.user.memberId
          }/headers`,
        )
        .then((response) => {
          context.commit('UPDATE_HEADERS', response.data);
          return response.data;
        });
    },
  },
  mutations: {
    UPDATE_HEADERS(state: CoreState, data: HeadersResponseModel): void {
      state.user = Object.assign(state.user, data);
    },
    SET_CHOOSE_GROUP(state: CoreState): void {
      state.chooseGroup = true;
    },
    SET_MEMBER_MIGRATION_COMPLETE(state: CoreState): void {
      state.memberMigrationComplete = true;
    },
    SET_USER_GROUPS(
      state: CoreState,
      userGroups: UserGroupResponseModel[],
    ): void {
      state.userGroups = userGroups;
    },
    setMemberPreferenceInformation(
      state: CoreState,
      preferences: MemberPreferenceInformationResponseModel,
    ): void {
      state.memberPreferenceInformation = preferences;
    },
    loginUser(state: CoreState, user: UserModel): void {
      state.user = user;
    },
    updateHeaders(state: CoreState, headers: HeadersResponseModel): void {
      state.user = Object.assign(state.user, headers);
    },
    setPreviousRoute(state: CoreState, route: string): void {
      state.previousRoute = route;
    },
    setNotifications(
      state: CoreState,
      notifications: MemberMessageResponseModel[],
    ): void {
      state.notifications = notifications;
    },
    setNotificationAsRead(state: CoreState, messageId: string): void {
      const SELECTED_NOTIFICATION = state.notifications.find(
        (notification: MemberMessageResponseModel) => {
          return notification.messageId === messageId;
        },
      );

      SELECTED_NOTIFICATION.isReadIndicator = true;
    },
    hideAnnouncement(state: CoreState): void {
      state.showAnnouncement = false;
    },
  },
  getters: {
    user(state): UserModel {
      return state.user;
    },
    username(state): string {
      return state.user ? state.user.username : null;
    },
    memberId(state): string {
      return state.user ? state.user.memberId : null;
    },
    email(state): string {
      return state.user ? state.user.emailAddress : null;
    },
    firstName(state): string {
      return state.user ? state.user.firstName : null;
    },
    lastName(state): string {
      return state.user ? state.user.lastName : null;
    },
    previousRoute(state): string {
      return state.previousRoute;
    },
    notifications(state): MemberMessageResponseModel[] {
      return state.notifications.sort((a, b) => {
        if (
          moment(b.postingDate, dateFormat).isBefore(
            moment(a.postingDate, dateFormat),
          )
        ) {
          return -1;
        } else if (
          moment(a.postingDate, dateFormat).isBefore(
            moment(b.postingDate, dateFormat),
          )
        ) {
          return 1;
        } else {
          const TEXT_A = a.message.toUpperCase();
          const TEXT_B = b.message.toUpperCase();
          return TEXT_A < TEXT_B ? -1 : TEXT_A > TEXT_B ? 1 : 0;
        }
      });
    },
    alerts(state): any[] {
      return state.alerts;
    },
    userJWT(state) {
      return state.user ? state.user.jwt : null;
    },
    carrier(state): string {
      return state.carrier;
    },
    memberPreferenceInformation(
      state,
    ): MemberPreferenceInformationResponseModel {
      return state.memberPreferenceInformation;
    },
    isCobranded(state): boolean {
      return state.isCobranded;
    },
    getChooseGroup(state): boolean {
      return state.chooseGroup;
    },
    getMemberMigrationComplete(state): boolean {
      return state.memberMigrationComplete;
    },
    userGroups(state): UserGroupResponseModel[] {
      return state.userGroups;
    },
    showAnnouncement(state): boolean {
      return state.showAnnouncement;
    },
  },
};

export default CORE_STATE;
